/**
 * Detect the academic level of a degree and of a scholarship record,
 * so we don't show Masters-only awards on undergrad course pages
 * (or vice-versa). Both inputs are messy free-text — no structured
 * `level` field exists on `d.scholarships` or `uni.scholarships_available`
 * — so we run a keyword classifier.
 *
 * Return values:
 *   - degreeLevel:      "ug" | "pg" | "unknown"
 *   - scholarshipLevel: "ug" | "pg" | "any"
 *
 * A scholarship is only dropped when we're CONFIDENT it's for the wrong
 * level. Anything ambiguous falls into "any" and is always shown.
 */

// --- Degree side --------------------------------------------------------

const UG_QUAL_RE = /\b(ba|bs|bsc|beng|bcom|blaw|llb|bmus|bfa|bed|barch|bam|bmed|bm|bch|bds|bvsc|ab|sb|foundation)\b/i;
const PG_QUAL_RE = /\b(ma|msc|meng|mphil|mres|mba|mlitt|mst|msci|mfa|mmus|med|llm|mch|md|phd|dphil|edd|doctorate|postgrad|graduate)\b/i;

// Integrated undergraduate masters (MEng, MSci, MMath, MChem) are FOUR-year
// UG programmes in the UK — they carry an "M" qualification code but any
// scholarship advertised for postgraduate applicants does NOT apply. If a
// student is applying school-leaver, this is an undergrad path.
const INTEGRATED_UG_RE = /\b(m[a-z]{2,4})\b/i;

export function degreeLevel(qualification, durationYears) {
  if (!qualification || typeof qualification !== "string") return "unknown";
  const q = qualification.toLowerCase();

  // "BA (Hons) / MCompSci (4-year option)" — has both B* and M*.
  // If it also mentions a bachelor code or 4-year duration → treat as UG.
  const hasUgCode = UG_QUAL_RE.test(q);
  const hasPgCode = PG_QUAL_RE.test(q);

  if (hasUgCode && !hasPgCode) return "ug";
  if (hasPgCode && !hasUgCode) {
    // Distinguish integrated UG masters (MEng/MSci/MMath) from taught PG masters.
    // Integrated variants are typically 4-year courses taken straight from school.
    if (INTEGRATED_UG_RE.test(q) && (durationYears || 0) >= 4 && (durationYears || 0) <= 5) {
      return "ug";
    }
    return "pg";
  }
  if (hasUgCode && hasPgCode) return "ug"; // combined "BA / MEng" style → UG entry
  return "unknown";
}

// --- Scholarship side ---------------------------------------------------

// PG-only signals — must be pretty specific to avoid false positives.
// The bare word "graduate" is a strong PG signal in scholarship context
// (e.g. "Graduate students only") but we must NOT match it inside
// "undergraduate". JS supports lookbehind so we exclude that explicitly.
const PG_ONLY_RE = new RegExp(
  [
    /* full words / phrases */
    "postgraduate", "post[- ]graduate",
    "(?<!under)\\bgraduate\\b",       // bare "graduate" but not "undergraduate"
    "graduate school", "graduate program(?:me)?",
    "master'?s", "masters degree", "masters programme", "masters program",
    "msc\\b", "m\\.?sc\\b", "mphil\\b", "mres\\b", "mba\\b", "m\\.?b\\.?a\\b",
    "llm\\b", "l\\.l\\.m\\b",
    "phd\\b", "ph\\.?d\\b", "dphil\\b", "doctoral", "doctorate",
    "research student",
  ].join("|"),
  "i",
);

// UG-only signals. Note: bare "bachelor" is only counted when clearly
// combined with a level phrase; loose word matches like just "BA" alone are
// too noisy inside eligibility text so we skip them.
const UG_ONLY_RE = new RegExp(
  [
    "undergraduate",
    "bachelor'?s degree", "bachelors degree", "bachelor'?s programme", "bachelor'?s program",
    "first[- ]year students?", "first[- ]time freshman", "freshman applicants?",
    "school[- ]leavers?", "high[- ]school graduates?",
    "a[- ]?level(?:s)?",
    "high school seniors?",
  ].join("|"),
  "i",
);

function _fields(sch) {
  return [
    sch?.name,
    sch?.eligibility,
    sch?.criteria,
    sch?.description,
    // Also concat tags array if present.
    Array.isArray(sch?.tags) ? sch.tags.join(" ") : sch?.tags,
  ]
    .filter(Boolean)
    .join(" | ");
}

export function scholarshipLevel(sch) {
  const text = _fields(sch);
  if (!text) return "any";
  const isPg = PG_ONLY_RE.test(text);
  const isUg = UG_ONLY_RE.test(text);
  if (isPg && !isUg) return "pg";
  if (isUg && !isPg) return "ug";
  return "any";
}

// --- Filter -------------------------------------------------------------

/**
 * Drop scholarships that clearly don't apply to this degree's level.
 * Keeps "any"/unknown-level items so we never over-filter genuine awards.
 */
export function filterScholarshipsByLevel(scholarships, qualification, durationYears) {
  if (!Array.isArray(scholarships) || scholarships.length === 0) return [];
  const dl = degreeLevel(qualification, durationYears);
  if (dl === "unknown") return scholarships;
  return scholarships.filter((s) => {
    const sl = scholarshipLevel(s);
    if (sl === "any") return true;
    return sl === dl;
  });
}
